import { EthAddress } from '@xylabs/sdk-js'
import { useAsyncEffect } from '@xylabs/sdk-react'
import { XyoWorldBidder, XyoWorldGeotokenWrapper } from '@xyo-network/sdk-xyoworld-js'
import BigNumber from 'bn.js'
import { PropsWithChildren, useContext, useEffect, useState } from 'react'

import { parseBigNumberOptional } from '../../parseBigNumber'
import { CurrencyContext } from '../Currency'
import { EthersContext } from '../Ethers'
import { GeotokensContext } from '../Geotokens'
import { useMinter } from '../Minter'
import { BidderContext } from './Context'

interface Props {
  address?: EthAddress | null
  enabled?: boolean
}

export const BidderLoader: React.FC<PropsWithChildren<Props>> = (props) => {
  const { localAddress } = useContext(EthersContext)
  const address = props.address ?? localAddress
  const { minter, minterVersion1, minterVersion2 } = useMinter()
  const { erc20 } = useContext(CurrencyContext)
  const { geotokens } = useContext(GeotokensContext)
  const enabled = true

  const [bidder, setBidder] = useState<XyoWorldBidder>()
  const [pendingCount, setPendingCount] = useState<number>()
  const [totalBids, setTotalBids] = useState<number>()
  const [geoTokensBidOn, setGeoTokensBidOn] = useState<number>()
  const [currentBalance, setCurrentBalance] = useState<BigNumber>()
  const [currentAllowance, setCurrentAllowance] = useState<BigNumber>()
  const [maxXyo, setMaxXyo] = useState<BigNumber>()
  const [geotokensOwned, setGeotokensOwned] = useState<XyoWorldGeotokenWrapper[]>()
  const [xyoInGeotokens, setXyoInGeotokens] = useState<BigNumber>()

  useEffect(() => {
    let bidderWrapper: XyoWorldBidder | undefined = undefined
    if (minter && erc20 && geotokens && address && enabled) {
      bidderWrapper = new XyoWorldBidder(address, minter)
    }
    setBidder(bidderWrapper)
  }, [minter, erc20, geotokens, address, minterVersion1, minterVersion2, enabled])

  useEffect(() => {
    let cancelled = false
    const load = () => {
      const pendingCount = 0
      if (!cancelled) {
        setPendingCount(pendingCount)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [bidder])

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (enabled) {
        const totalBids = (await bidder?.getBids())?.length ?? 0
        const geoTokensBidOn = (await bidder?.getQuadkeysBidOn())?.length ?? 0
        if (mounted()) {
          setTotalBids(totalBids)
          setGeoTokensBidOn(geoTokensBidOn)
        }
      }
    },
    [bidder, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (enabled) {
        const currentBalance = parseBigNumberOptional(await bidder?.getBalance())
        const currentAllowance = parseBigNumberOptional(await bidder?.getAllowance())
        const maxXyo = parseBigNumberOptional(await bidder?.getMaxCurrency())
        if (mounted()) {
          setCurrentBalance(currentBalance)
          setCurrentAllowance(currentAllowance)
          setMaxXyo(maxXyo)
        }
      }
    },
    [bidder, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (mounted) => {
      if (enabled) {
        const geotokensOwned = await bidder?.getOwnedGeotokens()
        const xyoInGeotokens = parseBigNumberOptional(await bidder?.getTotalCurrencyInOwnedGeotokens())
        if (mounted()) {
          setGeotokensOwned(geotokensOwned)
          setXyoInGeotokens(xyoInGeotokens)
        }
      }
    },
    [bidder, enabled],
  )

  return (
    <BidderContext.Provider
      value={{
        bidder,
        currentAllowance,
        currentBalance,
        geoTokensBidOn,
        geotokensOwned,
        maxXyo,
        pendingCount,
        totalBids,
        xyoInGeotokens,
      }}
    >
      {props.children}
    </BidderContext.Provider>
  )
}
